import { Dispatch, SetStateAction, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, CircularProgress, Modal } from "@mui/material";
import OTPInput from "react-otp-input";
import { useRouter } from "next/navigation";
import clsx from "clsx";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  otp: string;
  setOtp: Dispatch<SetStateAction<string>>;
  countDown: number;
  setCountDown: Dispatch<SetStateAction<number>>;
  title?: string;
  description?: string;
  onSubmit?: () => void;
  onResend?: () => void;
  loading?: boolean;
}

export default function OtpVerifyModal({
  open,
  onClose,
  setOpen,
  title = "Verify Email Address",
  description = "A verification OTP has been sent to your email address. Please check your inbox.",
  onSubmit,
  onResend,
  countDown,
  otp,
  setCountDown,
  setOtp,
  loading,
}: ModalProps) {
  const [isResending, setIsResending] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (countDown <= 0) return;

    const interval = setInterval(() => {
      setCountDown((prev) => {
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, countDown]);

  return (
    <Modal className="modal otpVerify_modal" open={open} onClose={onClose}>
      <div className="modal-dialog">
        <div className="modal-body">
          <div
            className="btn-close"
            onClick={() => {
              setCountDown(59);
              setOpen(false);
            }}
          >
            <CloseIcon />
          </div>
          <div className="modal_title hd_4">
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log("Form submit prevented, calling onSubmit");
              if (onSubmit) {
                onSubmit();
              }
            }}
          >
            <div className="gap_p">
              <div className="control_group opt_fields w_100">
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={4}
                  renderInput={(props) => <input {...props} />}
                  shouldAutoFocus
                  inputType="tel"
                />
                <Box component="a" className="resend">
                  {countDown === 0 && !loading && !isResending ? (
                    <>
                      <div />
                      <strong
                        onClick={async () => {
                          if (!onResend || isResending) return;
                          setIsResending(true);
                          setCountDown(59);
                          setOtp("");
                          try {
                            await onResend();
                          } finally {
                            setIsResending(false);
                          }
                        }}
                      >
                        Resend
                      </strong>
                    </>
                  ) : (
                    <>
                      {isResending ? "Sending..." : `00:${countDown
                        .toString()
                        .padStart(2, "0")}`}
                    </>
                  )}{" "}
                </Box>
              </div>
            </div>
            <div className="btn_flex">
              <Button
                color="primary"
                className={clsx("", {
                  "w_100 disabled-button": loading,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("Submit button clicked in OTP modal");
                  if (onSubmit) {
                    console.log("Calling onSubmit function");
                    onSubmit();
                  } else {
                    console.error("onSubmit is not defined!");
                  }
                }}
                disabled={loading}
                type="button"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" /> Submitting
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
